/**
 * Lightweight rich-text editor (contentEditable + execCommand) for the
 * quarter narrative: bold / italic / underline / highlight colours /
 * heading / bullets / clear formatting. Stores HTML. Also exports the
 * markdown-lite -> HTML converter used for fresh AI drafts and legacy
 * plain-text summaries, plus the shared content styles.
 */
import { useEffect, useRef, useState } from "react";
import {
  Bold,
  Eraser,
  Heading3,
  Highlighter,
  Italic,
  List,
  Smile,
  Underline,
} from "lucide-react";

export const RICH_STYLES = `
  .qtr-rich { font-size: 0.875rem; color: #334155; line-height: 1.7; }
  .qtr-rich p { margin: 0.55rem 0; }
  .qtr-rich h3 { font-family: "Cormorant Garamond", "Georgia", serif; font-size: 1.35rem; font-weight: 500; color: #0f172a; margin: 1rem 0 0.35rem; }
  .qtr-rich ul { list-style: none; padding: 0; margin: 0.55rem 0; }
  .qtr-rich li { padding-left: 0.75rem; border-left: 2px solid rgba(31,116,104,0.25); margin: 0.45rem 0; }
  .qtr-rich mark, .qtr-rich span[style*="background"] { padding: 0 0.15em; border-radius: 2px; }
  .qtr-rich u { text-underline-offset: 2px; }
`;

const escapeHtml = (s) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const looksLikeHtml = (s) =>
  /<\/?(p|h3|ul|ol|li|strong|em|u|b|i|mark|span|div|br)[\s/>]/i.test(s || "");

export const mdliteToHtml = (text) => {
  const out = [];
  let bullets = null;
  String(text || "").split("\n").forEach((raw) => {
    const line = raw.trim();
    if (!line) {
      bullets = null;
      return;
    }
    if (line.startsWith("## ")) {
      bullets = null;
      out.push(`<h3>${escapeHtml(line.slice(3).trim())}</h3>`);
    } else if (/^[-•*]\s+/.test(line)) {
      const item = `<li>${escapeHtml(line.replace(/^[-•*]\s+/, ""))}</li>`;
      if (bullets === null) {
        out.push(`<ul>${item}</ul>`);
        bullets = out.length - 1;
      } else {
        out[bullets] = out[bullets].replace(/<\/ul>$/, `${item}</ul>`);
      }
    } else {
      bullets = null;
      out.push(`<p>${escapeHtml(line)}</p>`);
    }
  });
  return out.join("");
};

const EMOJIS = [
  "🎉", "🚀", "⭐", "🏆", "💪", "👏", "✅", "🔥",
  "📈", "📉", "💹", "📊", "💷", "💰", "💸", "💳",
  "🏦", "🧾", "🪙", "⚖️", "🤝", "💼", "👔", "🧑‍💼",
  "📝", "📋", "📌", "📎", "🖊️", "✍️", "📁", "🗂️",
  "📅", "🗓️", "⏱️", "⏰", "⏳", "🚦", "🎯", "💡",
  "📞", "📧", "✉️", "💻", "🖥️", "🏢", "🌍", "✈️",
  "⚠️", "🔍", "⚙️", "🛠️", "🔑", "🔒", "📣", "🔔",
  "☀️", "🌧️", "🔭", "🌱", "❤️", "😊", "🥂", "🍾",
];

export const POSITIVE_EMOJIS = [
  "🎉", "🥳", "🎊", "🎈", "🚀", "⭐", "🌟", "✨",
  "🏆", "🥇", "🏅", "🎖️", "💪", "👏", "🙌", "👍",
  "😄", "😊", "🤩", "😎", "💚", "❤️", "🔥", "⚡",
  "📈", "💹", "💰", "💷", "💸", "🤑", "💎", "🪙",
  "✅", "🎯", "🤝", "🫶", "🥂", "🍾", "🎁", "🌈",
  "☀️", "🌞", "🌱", "🌸", "🦾", "💡", "📣", "🔔",
];

export const NEGATIVE_EMOJIS = [
  "📉", "🔻", "⚠️", "🚨", "❌", "⛔", "🛑", "🚧",
  "😟", "😕", "🫤", "😬", "😢", "😤", "🤦", "👎",
  "💔", "😮‍💨", "😰", "😓", "🥶", "🥴", "🤕", "🙈",
  "🌧️", "⛈️", "🌩️", "🌫️", "🌪️", "🐌", "🪫", "🧊",
  "💸", "🧯", "🆘", "❗", "‼️", "⏳", "🕳️", "💤",
];


const HIGHLIGHTS = [
  { name: "yellow", color: "#fde68a" },
  { name: "green", color: "#bbf7d0" },
  { name: "blue", color: "#bfdbfe" },
  { name: "pink", color: "#fbcfe8" },
  { name: "none", color: "transparent" },
];

function ToolButton({ title, testid, onClick, children, active }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testid}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border text-slate-600 transition-colors ${
        active
          ? "border-[#1f7468] bg-[#1f7468]/10 text-[#1f7468]"
          : "border-stone-200 bg-white hover:border-[#1f7468]/50 hover:text-[#1f7468]"
      }`}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, placeholder, testid, minHeight = "16rem", idPrefix = "rte", emojis = EMOJIS }) {
  const ref = useRef(null);
  const [showHighlights, setShowHighlights] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(ref.current?.innerHTML || "");
  };

  const insertEmoji = (emoji) => {
    const el = ref.current;
    if (!el) return;
    const sel = window.getSelection();
    const inside = sel?.anchorNode && el.contains(sel.anchorNode);
    el.focus();
    if (!inside) {
      // no caret in the editor yet - drop the emoji at the end
      const r = document.createRange();
      r.selectNodeContents(el);
      r.collapse(false);
      sel.removeAllRanges();
      sel.addRange(r);
    }
    document.execCommand("insertText", false, emoji);
    onChange(el.innerHTML || "");
  };

  const toggleHeading = () => {
    const cur = (document.queryCommandValue("formatBlock") || "").toLowerCase();
    exec("formatBlock", cur === "h3" ? "<p>" : "<h3>");
  };

  return (
    <div className="mt-3">
      <style>{RICH_STYLES}</style>
      <div className="flex items-center gap-1.5 flex-wrap rounded-t-xl border border-b-0 border-stone-200 bg-stone-50 px-2 py-1.5">
        <ToolButton title="Bold" testid={`${idPrefix}-bold-btn`} onClick={() => exec("bold")}>
          <Bold className="w-3.5 h-3.5" />
        </ToolButton>
        <ToolButton title="Italic" testid={`${idPrefix}-italic-btn`} onClick={() => exec("italic")}>
          <Italic className="w-3.5 h-3.5" />
        </ToolButton>
        <ToolButton title="Underline" testid={`${idPrefix}-underline-btn`} onClick={() => exec("underline")}>
          <Underline className="w-3.5 h-3.5" />
        </ToolButton>
        <div className="relative">
          <ToolButton
            title="Highlight"
            testid={`${idPrefix}-highlight-btn`}
            active={showHighlights}
            onClick={() => {
              setShowEmojis(false);
              setShowHighlights((v) => !v);
            }}
          >
            <Highlighter className="w-3.5 h-3.5" />
          </ToolButton>
          {showHighlights && (
            <div
              className="absolute z-20 top-9 left-0 flex items-center gap-1.5 rounded-xl border border-stone-200 bg-white shadow-lg p-2"
              data-testid={`${idPrefix}-highlight-palette`}
            >
              {HIGHLIGHTS.map((h) => (
                <button
                  key={h.name}
                  type="button"
                  title={h.name === "none" ? "Remove highlight" : `Highlight ${h.name}`}
                  data-testid={`${idPrefix}-highlight-${h.name}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    exec("hiliteColor", h.color);
                    setShowHighlights(false);
                  }}
                  className={`w-6 h-6 rounded-md border ${
                    h.name === "none"
                      ? "border-stone-300 bg-white relative overflow-hidden"
                      : "border-stone-200"
                  }`}
                  style={h.name === "none" ? {} : { backgroundColor: h.color }}
                >
                  {h.name === "none" && (
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] text-stone-400">
                      ✕
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="w-px h-5 bg-stone-200 mx-0.5" />
        <ToolButton title="Heading" testid={`${idPrefix}-heading-btn`} onClick={toggleHeading}>
          <Heading3 className="w-3.5 h-3.5" />
        </ToolButton>
        <ToolButton title="Bullet list" testid={`${idPrefix}-bullets-btn`} onClick={() => exec("insertUnorderedList")}>
          <List className="w-3.5 h-3.5" />
        </ToolButton>
        <span className="w-px h-5 bg-stone-200 mx-0.5" />
        <div className="relative">
          <ToolButton
            title="Emoji"
            testid={`${idPrefix}-emoji-btn`}
            active={showEmojis}
            onClick={() => {
              setShowHighlights(false);
              setShowEmojis((v) => !v);
            }}
          >
            <Smile className="w-3.5 h-3.5" />
          </ToolButton>
          {showEmojis && (
            <div
              className="absolute z-20 top-9 left-0 grid grid-cols-8 gap-0.5 rounded-xl border border-stone-200 bg-white shadow-lg p-2 w-64"
              data-testid={`${idPrefix}-emoji-palette`}
            >
              {emojis.map((em) => (
                <button
                  key={em}
                  type="button"
                  data-testid={`${idPrefix}-emoji-${em.codePointAt(0).toString(16)}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    insertEmoji(em);
                    setShowEmojis(false);
                  }}
                  className="w-7 h-7 rounded-md text-base leading-none hover:bg-stone-100 transition-colors"
                >
                  {em}
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="w-px h-5 bg-stone-200 mx-0.5" />
        <ToolButton
          title="Clear formatting"
          testid={`${idPrefix}-clear-btn`}
          onClick={() => {
            exec("removeFormat");
            exec("formatBlock", "<p>");
          }}
        >
          <Eraser className="w-3.5 h-3.5" />
        </ToolButton>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-testid={testid}
        data-placeholder={placeholder}
        onInput={() => onChange(ref.current?.innerHTML || "")}
        onBlur={() => setTimeout(() => { setShowHighlights(false); setShowEmojis(false); }, 150)}
        style={{ minHeight }}
        className="qtr-rich rte-area max-h-[34rem] overflow-y-auto border border-stone-200 rounded-b-xl px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#1f7468]/30"
      />
      <style>{`
        .rte-area:empty:before { content: attr(data-placeholder); color: #a8a29e; font-size: 0.875rem; pointer-events: none; }
      `}</style>
    </div>
  );
}
